import { useState, useEffect, useRef } from "react";
import {
  getUserGroups,
  getUsers,
  getDepartments,
  createUserGroup,
  updateUserGroup,
  deleteUserGroup,
  updateGroupMembers,
} from "../api/UserGroupsApi";
import CulsightPageLoader from "../components/ui/CulsightLoader";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Search, Users, Trash2, Edit, UserCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { name: "", description: "" };

// Backend jo query key maangta hai wo yaha rakho
const SEARCH_KEY = "search";

// Backend ke response se error message nikalta hai (string / FastAPI 422 array / object / network error)
const getErrorMessage = (err, fallback) => {
  const data = err.response?.data;
  const detail = data?.detail ?? data?.message ?? data?.error;

  if (typeof detail === "string") return detail;

  // FastAPI 422: [{ loc: [...], msg: "..." }]
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "";
        return field ? `${field}: ${d.msg}` : d.msg || JSON.stringify(d);
      })
      .join(", ");
  }

  if (detail) return JSON.stringify(detail);
  return err.message || fallback; // response hi nahi aaya (network error etc.)
};

const UserGroups = () => {
  const [groups, setGroups] = useState([]);
  const [memberUsers, setMemberUsers] = useState([]); // backend se filter ho kar aaye users
  const [totalUsers, setTotalUsers] = useState(0);
  const [membersLoading, setMembersLoading] = useState(false);
  const memberReqRef = useRef(0);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const isFirstSearchRender = useRef(true);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [savingGroup, setSavingGroup] = useState(false);

  // Members management
  const [membersDialog, setMembersDialog] = useState(null); // group obj
  const [deleteDialog, setDeleteDialog] = useState(null); // group to delete
  const [deleting, setDeleting] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState("");
  const [memberDept, setMemberDept] = useState("all");
  const [deptOpen, setDeptOpen] = useState(false);
  const [deptQuery, setDeptQuery] = useState("");
  const deptRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [savingMembers, setSavingMembers] = useState(false);

  useEffect(() => {
    fetchAll();
    fetchDepartments();
  }, []);

  // Department dropdown: bahar click karne par band ho jaye
  useEffect(() => {
    const handler = (e) => {
      if (deptRef.current && !deptRef.current.contains(e.target)) {
        setDeptOpen(false);
        setDeptQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Members search: typing ke 400ms baad
  useEffect(() => {
    const t = setTimeout(() => setDebouncedMemberSearch(memberSearch), 400);
    return () => clearTimeout(t);
  }, [memberSearch]);

  // Dialog khulte hi, ya department / search badalte hi backend call
  useEffect(() => {
    if (!membersDialog) return;
    fetchMemberUsers(memberDept, debouncedMemberSearch);
  }, [membersDialog?.id, memberDept, debouncedMemberSearch]);

  // Group search: typing ke 400ms baad backend call
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    fetchGroups(debouncedSearch);
  }, [debouncedSearch]);

  const fetchGroups = async (q = debouncedSearch) => {
    try {
      const g = await getUserGroups(q ? { [SEARCH_KEY]: q } : {});
      setGroups(g.data.results);
    } catch (e) {
      toast.error(getErrorMessage(e, "Failed to load groups"));
    }
  };

  // Members dialog ke users backend se aate hain: /users/?department=X&search=Y
  const fetchMemberUsers = async (dept, q) => {
    const myId = ++memberReqRef.current;
    setMembersLoading(true);
    try {
      const res = await getUsers(dept, q ? { [SEARCH_KEY]: q } : {});
      if (myId !== memberReqRef.current) return;
      const list = res.data.results || [];
      setMemberUsers(list);
      // bina filter ke jo count aaya wahi total
      if (dept === "all" && !q) setTotalUsers(res.data.count ?? list.length);
    } catch (e) {
      if (myId === memberReqRef.current) toast.error(getErrorMessage(e, "Failed to load users"));
    } finally {
      if (myId === memberReqRef.current) setMembersLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await getDepartments();
      const list = res.data.results || res.data || [];
      setDepartments(list.map((d) => d.name));
    } catch (e) {
      toast.error(getErrorMessage(e, "Failed to load departments"));
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const g = await getUserGroups(debouncedSearch ? { [SEARCH_KEY]: debouncedSearch } : {});
      setGroups(g.data.results);
    } catch (e) {
      toast.error(getErrorMessage(e, "Failed to load groups"));
    }
    setLoading(false);
  };

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setGroupDialogOpen(true); };
  const openEdit = (g) => { setEditingId(g.id); setForm({ name: g.name, description: g.description || "" }); setGroupDialogOpen(true); };

  // Frontend validation nahi hai: Create dabate hi API hit hoti hai, error backend ke response se dikhta hai
  const submitGroup = async (e) => {
    e.preventDefault();
    setSavingGroup(true);

    try {
      if (editingId) {
        await updateUserGroup(editingId, form);
        toast.success("Group updated");
      } else {
        await createUserGroup({
          ...form,
          member_ids: [],
        });
        toast.success("Group created");
      }

      setGroupDialogOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to save group"));
    } finally {
      setSavingGroup(false);
    }
  };

  const deleteGroup = async () => {
    if (!deleteDialog) return;

    setDeleting(true);

    try {
      await deleteUserGroup(deleteDialog.id);
      toast.success("Group deleted");
      setDeleteDialog(null);
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete group"));
    } finally {
      setDeleting(false);
    }
  };

  const openMembers = (group) => {
    setMembersDialog(group);
    setSelectedIds(new Set(group.member_ids || []));
    setMemberSearch("");
    setDebouncedMemberSearch("");
    setMemberDept("all");
    setDeptOpen(false);
    setDeptQuery("");
  };

  const toggleMember = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const saveMembers = async () => {
    if (!membersDialog) return;
    setSavingMembers(true);
    try {
      await updateGroupMembers(membersDialog.id, {
        member_ids: Array.from(selectedIds),
      });
      toast.success(`Saved ${selectedIds.size} member(s)`);
      setMembersDialog(null);
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to save members"));
    }
    setSavingMembers(false);
  };

  // Filter ab backend karta hai, yaha sirf list use hoti hai
  const filteredMemberUsers = Array.isArray(memberUsers) ? memberUsers : [];

  const filteredDepartments = departments.filter((d) =>
    d.toLowerCase().includes(deptQuery.toLowerCase())
  );

  const selectAllFiltered = () => {
    const next = new Set(selectedIds);
    filteredMemberUsers.forEach((u) => next.add(u.id));
    setSelectedIds(next);
  };

  const clearAllFiltered = () => {
    const next = new Set(selectedIds);
    filteredMemberUsers.forEach((u) => next.delete(u.id));
    setSelectedIds(next);
  };

  // Search ab backend se hota hai, client-side filter nahi
  const filteredGroups = Array.isArray(groups) ? groups : [];

  if (loading) {
    return <CulsightPageLoader />;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="user-groups-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">User Groups</h1>
          <p className="text-gray-500 text-sm mt-1">Organize users into groups for targeted campaigns</p>
        </div>
        <Button onClick={openCreate} className="bg-[#8c3add] hover:bg-[#702eb0] text-white gap-2" data-testid="create-group-btn">
          <Plus size={18} /> New Group
        </Button>
      </div>

      {/* Stats */}
      <Card className="border border-gray-100 shadow-sm">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="p-3 rounded-xl bg-[#8c3add]/10">
            <Users size={20} className="text-[#8c3add]" />
          </div>
          <div>
            <p className="text-2xl font-bold text-[#1f1f23]">{groups.length}</p>
            <p className="text-xs text-gray-500">Total Groups</p>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <Input placeholder="Search groups..." className="pl-10" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="groups-search" />
      </div>

      {/* Groups Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="groups-grid">
        {filteredGroups.length === 0 ? (
          <div className="col-span-full py-12 text-center text-gray-500">No user groups found</div>
        ) : (
          filteredGroups.map((group) => (
            <Card key={group.id} className="border border-gray-100 shadow-sm card-hover" data-testid={`group-card-${group.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="p-2 rounded-lg bg-[#8c3add]/10">
                    <Users size={20} className="text-[#8c3add]" />
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {group.member_count || 0} {(group.member_count || 0) === 1 ? "member" : "members"}
                  </Badge>
                </div>

                <h3 className="font-semibold text-[#1f1f23] mt-3">{group.name}</h3>
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{group.description || "No description"}</p>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <span className="text-xs text-gray-400">{new Date(group.created_at).toLocaleDateString()}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="p-2 hover:bg-[#8c3add]/10" onClick={() => openMembers(group)} data-testid={`manage-members-${group.id}`} title="Manage members">
                      <UserCheck size={16} className="text-[#8c3add]" />
                    </Button>
                    <Button variant="ghost" size="sm" className="p-2 hover:bg-[#1fa7a1]/10" onClick={() => openEdit(group)} data-testid={`edit-group-${group.id}`}>
                      <Edit size={16} className="text-[#1fa7a1]" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 hover:bg-red-50"
                      onClick={() => setDeleteDialog(group)}
                      data-testid={`delete-group-${group.id}`}
                    >
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create / Edit Group Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={(o) => { setGroupDialogOpen(o); if (!o) { setEditingId(null); setForm(emptyForm); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Group" : "Create User Group"}</DialogTitle>
          </DialogHeader>
          {/* noValidate: browser ki validation band, validation backend karega */}
          <form onSubmit={submitGroup} noValidate className="space-y-3 mt-2">
            <div className="space-y-2">
              <Label>Group Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="group-description-input" />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={savingGroup}
                className="bg-[#8c3add] hover:bg-[#702eb0]"
                data-testid="group-submit-btn"
              >
                {savingGroup
                  ? <CulsightPageLoader />
                  : editingId
                    ? "Save Changes"
                    : "Create Group"
                }
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Members Dialog */}
      <Dialog open={!!membersDialog} onOpenChange={(o) => !o && setMembersDialog(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col" data-testid="members-dialog">
          <DialogHeader>
            <DialogTitle>
              Manage Members — {membersDialog?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2 flex-1 min-h-0 flex flex-col">
            <div className="flex items-stretch gap-2">
              {/* Search: bachi hui jagah le leta hai */}
              <div className="relative flex-1 min-w-[140px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <Input value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Search users..." className="pl-9" data-testid="member-search-input" />
              </div>

              <Button variant="outline" size="sm" className="shrink-0 h-auto" onClick={selectAllFiltered} data-testid="select-all-btn">Select all</Button>
              <Button variant="outline" size="sm" className="shrink-0 h-auto" onClick={clearAllFiltered}>Clear</Button>

              {/* Department dropdown: sabse right me, Clear ke baad */}
              <div className="flex items-center gap-2 shrink-0 pl-3 ml-1 border-l border-gray-200">
                <span className="text-sm font-medium text-[#1f1f23] whitespace-nowrap">Department:</span>
                <div className="relative w-48" ref={deptRef}>
                  <Input
                    value={deptOpen ? deptQuery : memberDept === "all" ? "" : memberDept}
                    placeholder={memberDept === "all" ? "Select Department" : memberDept}
                    onFocus={() => setDeptOpen(true)}
                    onChange={(e) => { setDeptQuery(e.target.value); setDeptOpen(true); }}
                    className="pr-9"
                    autoComplete="off"
                    data-testid="member-dept-input"
                  />
                  {/* Band hone par down arrow, khulne par search icon */}
                  {deptOpen ? (
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                  ) : (
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                  )}
                  {deptOpen && (
                    <div className="absolute right-0 top-full w-full mt-1 z-50 max-h-52 overflow-y-auto rounded-lg border bg-white shadow-lg p-1" data-testid="member-dept-list">
                      <button
                        type="button"
                        onClick={() => { setMemberDept("all"); setDeptOpen(false); setDeptQuery(""); }}
                        className={`w-full text-left text-sm px-3 py-2 rounded-md hover:bg-gray-100 ${memberDept === "all" ? "bg-[#8c3add]/10 text-[#8c3add] font-medium" : ""}`}
                      >
                        All Departments
                      </button>
                      {filteredDepartments.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-gray-500">No department found</p>
                      ) : (
                        filteredDepartments.map((dept) => (
                          <button
                            key={dept}
                            type="button"
                            onClick={() => { setMemberDept(dept); setDeptOpen(false); setDeptQuery(""); }}
                            className={`w-full text-left text-sm px-3 py-2 rounded-md hover:bg-gray-100 ${memberDept === dept ? "bg-[#8c3add]/10 text-[#8c3add] font-medium" : ""}`}
                          >
                            {dept}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Showing {filteredMemberUsers.length} of {totalUsers || filteredMemberUsers.length} users</span>
              <Badge className="bg-[#8c3add]/10 text-[#8c3add]" data-testid="selected-count">
                {selectedIds.size} selected
              </Badge>
            </div>
            <div className="relative border rounded-lg flex-1 overflow-y-auto" data-testid="members-list">
              {membersLoading && (
                <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
                  <CulsightPageLoader />
                </div>
              )}
              {filteredMemberUsers.length === 0 && !membersLoading ? (
                <p className="p-8 text-center text-gray-500 text-sm">No users match</p>
              ) : (
                filteredMemberUsers.map((u) => {
                  const checked = selectedIds.has(u.id);
                  return (
                    <label
                      key={u.id}
                      className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${checked ? "bg-[#8c3add]/5" : ""}`}
                      data-testid={`member-row-${u.id}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggleMember(u.id)} data-testid={`member-checkbox-${u.id}`} />
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="bg-[#8c3add]/10 text-[#8c3add] text-xs font-semibold">
                          {u.name.split(" ").map((n) => n[0]).join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1f1f23] truncate">{u.name}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">{u.department}</Badge>
                    </label>
                  );
                })
              )}
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setMembersDialog(null)}>Cancel</Button>
            <Button
              onClick={saveMembers}
              disabled={savingMembers}
              className="bg-[#8c3add] hover:bg-[#702eb0]"
              data-testid="save-members-btn"
            >
              {savingMembers ? <CulsightPageLoader /> : `Save ${selectedIds.size} member(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteDialog}
        onOpenChange={(open) => {
          if (!open) setDeleteDialog(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete User Group</DialogTitle>
          </DialogHeader>

          <div className="py-3">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-[#1f1f23]">
                "{deleteDialog?.name}"
              </span>
              ?
            </p>

            <p className="text-xs text-gray-500 mt-2">
              This action cannot be undone.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialog(null)}
              disabled={deleting}
            >
              Cancel
            </Button>

            <Button
              type="button"
              onClick={deleteGroup}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deleting ? <CulsightPageLoader /> : "Delete Group"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserGroups;